import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Alert, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Canonical severity values for DrawerMessage.
// Usage: severity={DRAWER_MESSAGE_SEVERITIES.ERROR}
export const DRAWER_MESSAGE_SEVERITIES = Object.freeze({
	ERROR: 'error',
	WARNING: 'warning',
	SUCCESS: 'success',
	INFO: 'info',
});

/**
 * DrawerMessage renders a non-scrolling, dismissible message strip for drawers.
 *
 * Severity options:
 * - 'error': destructive/action failed state
 * - 'warning': caution state requiring attention
 * - 'success': completed/action succeeded state
 * - 'info': neutral informative state
 */
function DrawerMessage({
	message,
	severity = DRAWER_MESSAGE_SEVERITIES.ERROR,
	fullWidth = true,
	sx,
	drawerSx,
}) {
	const [open, setOpen] = useState(Boolean(message));

	useEffect(() => {
		setOpen(Boolean(message));
	}, [message]);

	if (!message) {
		return null;
	}

	return (
		<Box
			sx={{
				px: 3,
				pt: 2,
				pb: 0,
				backgroundColor: 'background.paper',
				flexShrink: 0,
				...drawerSx,
			}}
		>
			<Collapse in={open} unmountOnExit>
				<Alert
					severity={severity}
					action={
						<IconButton
							aria-label="close"
							role="button"
							aria-hidden="false"
							color="inherit"
							size="small"
							onClick={() => setOpen(false)}
						>
							<CloseIcon
								fontSize="inherit"
								aria-label="close"
								role="graphics-symbol"
								aria-hidden="true"
							/>
						</IconButton>
					}
					sx={{
						width: fullWidth ? '100%' : '450px',
						maxWidth: '100%',
						marginLeft: 0,
						...sx,
					}}
				>
					{message}
				</Alert>
			</Collapse>
		</Box>
	);
}

DrawerMessage.propTypes = {
	message: PropTypes.string,
	severity: PropTypes.oneOf(Object.values(DRAWER_MESSAGE_SEVERITIES)),
	fullWidth: PropTypes.bool,
	sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	drawerSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default React.memo(DrawerMessage);
